import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentInstallmentService, PaymentGroupResponse } from '../services/paymentInstallmentService';
import customersService, { Customer } from '../services/clientsService';
import salesService from '../services/saleService';
import { SaleFunctionalUnit } from '../types';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  CreditCard,
  Banknote,
  CalendarDays,
  User,
  Building2,
  Hash,
  Loader2,
  AlertCircle,
  Receipt,
  Wallet,
  RefreshCw,
  ChevronRight,
  ShoppingBag,
  Layers,
  Home,
} from 'lucide-react';

interface SaleGroup {
  saleId: number;
  saleTotal: number;
  saleCurrency: string;
  deliveryDate: string | null;
  payments: PaymentGroupResponse[];
  functionalUnits: SaleFunctionalUnit[];
}

export default function CustomerPayments() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [groupedPayments, setGroupedPayments] = useState<PaymentGroupResponse[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [salesFuMap, setSalesFuMap] = useState<Map<number, SaleFunctionalUnit[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPastDue = useCallback((dueDate: string, isPaid: boolean) => {
    if (isPaid) return false;
    return new Date(dueDate) < new Date();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const customerIdNum = parseInt(customerId!);
      const [customerData, response, allSales] = await Promise.all([
        customersService.getCustomerById(customerIdNum),
        paymentInstallmentService.getCustomerInstallments(customerIdNum),
        salesService.getAllSales(),
      ]);
      setCustomer(customerData);
      setGroupedPayments(response);
      // Build a map saleId → functionalUnits for this customer
      const fuMap = new Map<number, SaleFunctionalUnit[]>();
      allSales
        .filter(s => s.customer?.customerId === customerIdNum)
        .forEach(s => fuMap.set(s.id, s.functionalUnits || []));
      setSalesFuMap(fuMap);
      setError(null);
    } catch (err) {
      setError('Error al cargar la información del cliente');
      console.error('Error fetching customer payments:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) fetchData();
  }, [customerId, fetchData]);

  const getCustomerName = (c: Customer) =>
    c.type === 'PERSON' ? `${c.firstName} ${c.lastName}` : c.businessName;

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency.toUpperCase() === 'USD' ? 'US$' : '$';
    return `${symbol} ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getPaymentTypeLabel = (type: string) =>
    ({ EFECTIVO: 'Efectivo', TRANSFERENCIA: 'Transferencia', CHEQUE: 'Cheque', FINANCIADO: 'Financiado', OTRO: 'Otro' }[type] || type);

  const getPaymentTypeIcon = (type: string) => {
    if (type === 'FINANCIADO') return <CalendarDays className="w-3.5 h-3.5" />;
    if (type === 'EFECTIVO') return <Banknote className="w-3.5 h-3.5" />;
    return <CreditCard className="w-3.5 h-3.5" />;
  };

  // Group payments by saleId
  const saleGroups = useMemo<SaleGroup[]>(() => {
    const map = new Map<number, SaleGroup>();
    for (const pg of groupedPayments) {
      const sid = pg.sale?.saleId ?? 0;
      if (!map.has(sid)) {
        map.set(sid, {
          saleId: sid,
          saleTotal: pg.sale?.total ?? pg.payment.totalAmount,
          saleCurrency: pg.sale?.currency ?? pg.payment.currency,
          deliveryDate: pg.sale?.deliveryDate ?? null,
          payments: [],
          functionalUnits: salesFuMap.get(sid) || [],
        });
      }
      map.get(sid)!.payments.push(pg);
    }
    return Array.from(map.values());
  }, [groupedPayments, salesFuMap]);

  // Global totals
  const totals = useMemo(() => {
    const all = groupedPayments.flatMap(g => g.installments);
    const total = all.reduce((s, i) => s + i.adjustedAmount, 0);
    const paid = all.filter(i => i.isPaid).reduce((s, i) => s + i.adjustedAmount, 0);
    const overdueItems = all.filter(i => !i.isPaid && isPastDue(i.dueDate, i.isPaid));
    const overdue = overdueItems.reduce((s, i) => s + i.adjustedAmount, 0);
    return {
      total, paid, pending: total - paid, overdue,
      overdueCount: overdueItems.length,
      paidCount: all.filter(i => i.isPaid).length,
      totalCount: all.length,
    };
  }, [groupedPayments, isPastDue]);

  const mainCurrency = groupedPayments[0]?.payment.currency || 'ARS';

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Cargando información de pagos...</p>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !customer) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <p className="text-sm font-medium text-destructive">{error || 'Cliente no encontrado'}</p>
          <Button onClick={() => navigate('/customer-payments')} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Cobranzas
          </Button>
        </div>
      </div>
    );
  }

  const isPerson = customer.type === 'PERSON';
  const progressPercent = totals.totalCount > 0 ? (totals.paidCount / totals.totalCount) * 100 : 0;

  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-4 py-5 border-b sm:px-6 border-border bg-gradient-to-r from-card to-muted/20">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/customer-payments')}
                className="flex items-center gap-2 rounded-lg shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Cobranzas
              </Button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPerson ? 'bg-primary/10' : 'bg-muted'}`}>
                    {isPerson ? <User className="w-4 h-4 text-primary" /> : <Building2 className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <h1 className="text-2xl font-bold text-foreground">{getCustomerName(customer)}</h1>
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md ${
                    isPerson ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isPerson ? 'Persona Física' : 'Empresa'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground font-mono mt-0.5">{customer.cuit}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="shrink-0">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-6 sm:p-6">

          {/* ── Banner de mora ── */}
          {totals.overdue > 0 && (
            <div className="flex items-center gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-destructive">
                  {totals.overdueCount} cuota{totals.overdueCount !== 1 ? 's' : ''} vencida{totals.overdueCount !== 1 ? 's' : ''} · {formatCurrency(totals.overdue, mainCurrency)}
                </p>
                <p className="text-xs text-destructive/70 mt-0.5">Requieren atención inmediata</p>
              </div>
            </div>
          )}

          {/* ── Stats globales ── */}
          <div className="bg-background border border-border rounded-xl overflow-hidden">
            {/* Progreso global */}
            <div className="p-5 border-b border-border">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  Progreso global · <span className="text-foreground font-semibold tabular-nums">{totals.paidCount}/{totals.totalCount}</span> cuotas
                </p>
                <div className="flex items-center gap-3 sm:min-w-[200px]">
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progressPercent === 100 ? 'bg-success' : progressPercent > 0 ? 'bg-primary' : 'bg-muted-foreground/20'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-foreground whitespace-nowrap">
                    {Math.round(progressPercent)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5">
              {[
                { label: 'Total', sub: `${saleGroups.length} venta${saleGroups.length !== 1 ? 's' : ''}`, value: formatCurrency(totals.total, mainCurrency), icon: Wallet, bg: 'bg-muted', color: 'text-muted-foreground', valueColor: 'text-foreground' },
                { label: 'Cobrado', sub: `${totals.paidCount} cuota${totals.paidCount !== 1 ? 's' : ''}`, value: formatCurrency(totals.paid, mainCurrency), icon: CheckCircle, bg: 'bg-success/10', color: 'text-success', valueColor: 'text-success' },
                { label: 'Pendiente', sub: 'Por cobrar', value: formatCurrency(totals.pending, mainCurrency), icon: Clock, bg: 'bg-info/10', color: 'text-info', valueColor: 'text-info' },
                { label: 'Vencido', sub: totals.overdue > 0 ? 'Requiere atención' : 'Sin mora', value: formatCurrency(totals.overdue, mainCurrency), icon: XCircle, bg: totals.overdue > 0 ? 'bg-destructive/10' : 'bg-muted', color: totals.overdue > 0 ? 'text-destructive' : 'text-muted-foreground', valueColor: totals.overdue > 0 ? 'text-destructive' : 'text-muted-foreground' },
              ].map((s) => (
                <div key={s.label} className={`p-4 border rounded-xl ${totals.overdue > 0 && s.label === 'Vencido' ? 'border-destructive/20 bg-destructive/5' : 'border-border bg-card'}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-sm font-medium text-muted-foreground leading-tight">{s.label}</p>
                    <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                      <s.icon className={`w-4 h-4 ${s.color}`} />
                    </div>
                  </div>
                  <p className={`text-2xl font-bold tabular-nums ${s.valueColor}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Tarjetas de ventas ── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold text-foreground">Ventas</h2>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                {saleGroups.length} {saleGroups.length === 1 ? 'venta' : 'ventas'}
              </span>
            </div>

            {saleGroups.length === 0 ? (
              <div className="bg-card border border-border rounded-xl flex flex-col items-center justify-center py-20 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-muted/50">
                  <Receipt className="w-8 h-8 text-muted-foreground/40" />
                </div>
                <p className="text-base font-medium text-muted-foreground">Sin ventas registradas</p>
                <p className="mt-1 text-sm text-muted-foreground/70">No hay pagos configurados para este cliente aún</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {saleGroups.map((sg) => {
                  const allInstallments = sg.payments.flatMap(p => p.installments);
                  const paidCount = allInstallments.filter(i => i.isPaid).length;
                  const totalCount = allInstallments.length;
                  const overdueCount = allInstallments.filter(i => isPastDue(i.dueDate, i.isPaid)).length;
                  const progressPct = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
                  const paidAmount = allInstallments.filter(i => i.isPaid).reduce((s, i) => s + i.adjustedAmount, 0);
                  const totalAmount = allInstallments.reduce((s, i) => s + i.adjustedAmount, 0);
                  const hasOverdue = overdueCount > 0;
                  const allPaid = paidCount === totalCount && totalCount > 0;

                  return (
                    <button
                      key={sg.saleId}
                      onClick={() => navigate(`/customer-payments/${customerId}/${sg.saleId}`)}
                      className={`group flex flex-col bg-card border rounded-xl overflow-hidden text-left w-full transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        hasOverdue
                          ? 'border-destructive/40 hover:border-destructive/60'
                          : allPaid
                          ? 'border-success/30 hover:border-success/50'
                          : 'border-border hover:border-primary/40'
                      }`}
                    >
                      {/* Card Header */}
                      <div className={`px-5 pt-5 pb-4 ${hasOverdue ? 'bg-destructive/5' : allPaid ? 'bg-success/5' : 'bg-muted/20'}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                              hasOverdue ? 'bg-destructive/10' : allPaid ? 'bg-success/10' : 'bg-primary/10'
                            }`}>
                              <ShoppingBag className={`w-5 h-5 ${hasOverdue ? 'text-destructive' : allPaid ? 'text-success' : 'text-primary'}`} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-base font-bold text-foreground">Venta #{sg.saleId}</p>
                              {sg.deliveryDate && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <Calendar className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">{formatDate(sg.deliveryDate)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total venta</p>
                            <p className="text-base font-bold text-foreground tabular-nums mt-0.5">
                              {formatCurrency(sg.saleTotal, sg.saleCurrency)}
                            </p>
                          </div>
                        </div>

                        {/* Status badges */}
                        <div className="flex flex-wrap gap-1.5">
                          {hasOverdue && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
                              <XCircle className="w-3 h-3" />
                              {overdueCount} vencida{overdueCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {allPaid && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-success/10 text-success">
                              <CheckCircle className="w-3 h-3" />
                              Cobrado completo
                            </span>
                          )}
                          {!allPaid && !hasOverdue && paidCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-info/10 text-info">
                              <Clock className="w-3 h-3" />
                              En curso
                            </span>
                          )}
                          {!allPaid && !hasOverdue && paidCount === 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              Pendiente
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            <Layers className="w-3 h-3" />
                            {sg.payments.length} tipo{sg.payments.length !== 1 ? 's' : ''} de pago
                          </span>
                        </div>
                      </div>

                      {/* Barra de progreso */}
                      <div className="px-5 py-3 border-t border-border/60">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {paidCount}/{totalCount} cuota{totalCount !== 1 ? 's' : ''} cobrada{paidCount !== 1 ? 's' : ''}
                          </span>
                          <span className="text-xs font-semibold text-foreground tabular-nums">{Math.round(progressPct)}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              progressPct === 100 ? 'bg-success' : hasOverdue ? 'bg-destructive' : 'bg-primary'
                            }`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[11px] text-muted-foreground">
                            Cobrado: <span className="font-medium text-foreground tabular-nums">{formatCurrency(paidAmount, sg.saleCurrency)}</span>
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            Pendiente: <span className="font-medium text-foreground tabular-nums">{formatCurrency(totalAmount - paidAmount, sg.saleCurrency)}</span>
                          </span>
                        </div>
                      </div>

                      {/* Tipos de pago chips */}
                      <div className="px-5 py-3 border-t border-border/60">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Medios de pago</p>
                        <div className="flex flex-wrap gap-1.5">
                          {sg.payments.map((pg) => {
                            const pgPaid = pg.installments.filter(i => i.isPaid).length;
                            const pgTotal = pg.installments.length;
                            const pgOverdue = pg.installments.some(i => isPastDue(i.dueDate, i.isPaid));
                            const pgAllPaid = pgPaid === pgTotal && pgTotal > 0;
                            return (
                              <span
                                key={pg.payment.paymentId}
                                className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg border ${
                                  pgOverdue
                                    ? 'bg-destructive/10 border-destructive/20 text-destructive'
                                    : pgAllPaid
                                    ? 'bg-success/10 border-success/20 text-success'
                                    : 'bg-muted border-border text-muted-foreground'
                                }`}
                              >
                                {getPaymentTypeIcon(pg.payment.paymentType)}
                                {getPaymentTypeLabel(pg.payment.paymentType)}
                                {pg.payment.totalInstallments && pg.payment.totalInstallments > 1 && (
                                  <span className="opacity-70">· {pgPaid}/{pgTotal}</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Unidades funcionales */}
                      {sg.functionalUnits.length > 0 && (
                        <div className="px-5 py-3 border-t border-border/60">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Unidades funcionales</p>
                          <div className="flex flex-wrap gap-1.5">
                            {sg.functionalUnits.map((fu) => (
                              <span
                                key={fu.functionalUnitId}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg border bg-muted border-border text-muted-foreground"
                              >
                                <Home className="w-3 h-3" />
                                <span className="font-semibold text-foreground">{fu.floor} {fu.letter}</span>
                                {fu.type && <span className="opacity-60">· {fu.type}</span>}
                                {fu.projectName && <span className="opacity-50">— {fu.projectName}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CTA */}
                      <div className="mt-auto px-5 py-3 border-t border-border/60 flex items-center justify-between bg-muted/10 group-hover:bg-muted/30 transition-colors">
                        <span className="text-xs font-medium text-primary">Ver detalle de cobranzas</span>
                        <ChevronRight className="w-4 h-4 text-primary transition-transform duration-200 group-hover:translate-x-0.5" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
